import { SubStatus } from 'enums/installation';
import { IInstallStepDef } from 'interfaces/i-installation';
import { InstallStep } from 'lib_ts/enums/installation';
import { InstallStepStatus } from 'lib_ts/enums/machine.enums';
import { IInstallationDetails, IMachine } from 'lib_ts/interfaces/i-machine';

export interface ICalibrationShotFlags {
  btcHasShots: boolean;
  rtHasShots: boolean;
}

export class InstallHelper {
  /* Only resume if we've fired shots for this step */
  private static subStatusForCalibrationStep(
    stepStatus: InstallStepStatus,
    hasShots: boolean
  ): SubStatus {
    if (hasShots && stepStatus !== InstallStepStatus.Complete) {
      return SubStatus.InProgress;
    }

    return SubStatus.NotStarted;
  }

  // uses the status of each step as per the details (for resuming)
  static getSteps(
    lastInstall: IInstallationDetails,
    machine: IMachine,
    shotFlags?: ICalibrationShotFlags
  ): IInstallStepDef[] {
    const output: IInstallStepDef[] = [
      {
        id: InstallStep.TestShot,
        label: 'Test Shot Firing',
        status: lastInstall.step_test_shot ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.TestShot,
      },
      {
        id: InstallStep.GeneralConfig,
        label: 'General Configuration',
        status: lastInstall.step_general_config ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.GeneralConfig,
      },
      {
        id: InstallStep.HardwareChanges,
        label: 'Hardware Changes',
        status:
          lastInstall.step_hardware_changes ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.HardwareChanges,
      },
      {
        id: InstallStep.ProjectorConfig,
        label: 'Projector Configuration',
        status:
          lastInstall.step_projector_config ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.ProjectorConfig,
      },
      {
        id: InstallStep.VisionCalibration,
        label: 'Trajekt Vision Calibration',
        status: lastInstall.step_vision ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        skip:
          !machine.enable_trajekt_vision ||
          (!!lastInstall.standalone &&
            lastInstall.standalone !== InstallStep.ProjectorConfig),
      },
      {
        id: InstallStep.BallType,
        label: 'Ball Type Calibration',
        status: lastInstall.step_ball_type ?? InstallStepStatus.Incomplete,
        subStatus: InstallHelper.subStatusForCalibrationStep(
          lastInstall.step_ball_type ?? InstallStepStatus.Incomplete,
          !!shotFlags?.btcHasShots
        ),
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.BallType,
      },
      {
        id: InstallStep.Repeatability,
        label: 'Repeatability Testing',
        status: lastInstall.step_repeatability ?? InstallStepStatus.Incomplete,
        subStatus: InstallHelper.subStatusForCalibrationStep(
          lastInstall.step_repeatability ?? InstallStepStatus.Incomplete,
          !!shotFlags?.rtHasShots
        ),
        skip:
          !!lastInstall.standalone &&
          lastInstall.standalone !== InstallStep.Repeatability,
      },
      {
        id: InstallStep.Completion,
        label: 'Completion',
        status: lastInstall.step_completion ?? InstallStepStatus.Incomplete,
        subStatus: SubStatus.NotStarted,
        // never show completion step for standalone instances
        skip: !!lastInstall.standalone,
      },
    ];

    // e.g. if vision is not enabled for a machine, then skip vision calibration
    return output.filter((s) => !s.skip);
  }
}
